import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { settingsService } from '../../services/settingsService';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    username: '',
    fullName: '',
    inviteCode: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [inviteCodeRequired, setInviteCodeRequired] = useState(false);
  const [checkingInviteCode, setCheckingInviteCode] = useState(true);
  
  const { signUp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Check if invite code is required
    const checkInviteCodeRequirement = async () => {
      try {
        const passcodeData = await settingsService?.getSignupPasscode();
        setInviteCodeRequired(passcodeData?.isRequired || false);
      } catch (err) {
        console.error('Error checking invite code requirement:', err);
        setInviteCodeRequired(false);
      } finally {
        setCheckingInviteCode(false);
      }
    };
    
    checkInviteCodeRequirement();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e?.target?.name]: e?.target?.value
    });
  };

  const validateForm = () => {
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (formData?.password?.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }
    if (!formData?.username?.trim()) {
      setError('Username is required');
      return false;
    }
    if (inviteCodeRequired && !formData?.inviteCode?.trim()) {
      setError('Invite code is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      // Validate invite code if required
      if (inviteCodeRequired) {
        const validation = await settingsService?.validatePasscode(formData?.inviteCode);
        
        if (!validation?.valid) {
          setError('Invalid invite code. Please check and try again.');
          setLoading(false);
          return;
        }
      }

      const { error } = await signUp(
        formData?.email,
        formData?.password,
        formData?.username,
        formData?.fullName || formData?.username
      );
      
      if (error) {
        setError(error?.message || 'Failed to create account');
      } else {
        setSuccess(true);
        setTimeout(() => {
          navigate('/auth/login');
        }, 2000);
      }
    } catch (err) {
      setError(err?.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-white bg-opacity-10">
              <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Registration Successful!
            </h2>
            <p className="mt-2 text-center text-sm text-blue-100">
              Please check your email for verification instructions.
              Redirecting to login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (checkingInviteCode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <LoadingSpinner text="Loading registration form..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {/* RoleCall Logo - Made twice as big */}
          <div className="mx-auto h-40 w-40 flex items-center justify-center mb-6">
            <img 
              src="/assets/images/rolecall_logo-1756785358409.png" 
              alt="RoleCall Logo" 
              className="h-full w-auto object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            Create your RoleCall account
          </h2>
          <p className="mt-2 text-center text-sm text-blue-100">
            Join the community
          </p>
          {inviteCodeRequired && (
            <div className="mt-3 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-lg p-3">
              <p className="text-center text-sm text-blue-100 flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                An invite code is required to sign up
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-lg p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
            
            <div className="space-y-4">
              {inviteCodeRequired && (
                <div>
                  <Input
                    id="inviteCode"
                    name="inviteCode"
                    type="text"
                    required
                    placeholder="Invite Code *"
                    value={formData?.inviteCode}
                    onChange={handleChange}
                    disabled={loading}
                    className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
                  />
                  <p className="mt-1 text-xs text-blue-200">
                    Enter the invite code provided by your administrator
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  id="username"
                  name="username"
                  type="text"
                  required
                  placeholder="Username"
                  value={formData?.username}
                  onChange={handleChange}
                  disabled={loading}
                  className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
                />
                
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Full Name (Optional)"
                  value={formData?.fullName}
                  onChange={handleChange}
                  disabled={loading}
                  className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
                />
              </div>
              
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                placeholder="Email address"
                value={formData?.email}
                onChange={handleChange}
                disabled={loading}
                className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
              />
              
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                placeholder="Password (min 6 characters)"
                value={formData?.password}
                onChange={handleChange}
                disabled={loading}
                className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
              />
              
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                placeholder="Confirm Password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                disabled={loading}
                className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200 rounded-lg"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Create Account'}
            </Button>

            <div className="text-center">
              <p className="text-sm text-blue-100">
                Already have an account?{' '}
                <Link 
                  to="/auth/login"
                  className="font-medium text-white hover:text-blue-200"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </form>
        </div>
        
        {/* Back Home Link - Moved to below the content box */}
        <div className="text-center mt-4">
          <Link 
            to="/"
            className="text-sm text-blue-200 hover:text-white underline"
          >
            Back Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;